/**
 * 轻量 i18n —— 按浏览器系统语言初始化，可手动切换并持久化。
 *
 * 用法：
 *   模板里： {{ $t('nav.home') }}   {{ $tp(item.cap) }}
 *   脚本里： const { t, tp } = useI18n()
 *
 * $t 查找顺序：当前语言 -> 简体中文(源语言) -> key 本身
 * $tp 用于翻译来自接口/ mock 的数据文案（天气状况、风向、月相等），
 *     未命中时原样返回，保证不会把数据弄丢。
 */
import { ref, computed, watch } from 'vue'

const STORAGE_KEY = 'app.locale'

/* ------------------------------------------------------------------ */
/* 语言包                                                              */
/* ------------------------------------------------------------------ */

const zhCN = {
  'app.title': 'Weather Live',
  'route.home': 'Weather Live',
  'route.location': '更改位置',
  'route.hourly': '每小时预报',
  'route.monthly': '天气预测',
  'route.map': '天气地图',
  'route.activity': '生活指数',
  'route.feed': '资讯',
  'route.article': '文章',
  'route.privacy': '隐私协议',
  'route.terms': '使用条款',
  'route.about': '关于我们',

  /* 导航 / 顶栏 */
  'nav.dialog': '导航菜单',
  'nav.open': '打开导航菜单',
  'nav.close': '关闭菜单',
  'nav.home': '天气首页',
  'nav.hourly': '每小时预报',
  'nav.monthly': '天气预测',
  'nav.activity': '生活指数',
  'nav.privacy': '隐私协议',
  'nav.terms': '使用条款',
  'nav.about': '关于我们',
  'nav.map': '天气地图',
  'nav.locating': '定位中…',
  'nav.useLocation': '使用当前位置',
  'nav.saved': '已保存的位置',
  'nav.manage': '管理位置',
  'nav.language': '语言',
  'appbar.search': '搜索',
  'appbar.unit': '温度单位',
  'appbar.brand': 'Weather Live',

  /* 首页 */
  'home.current': '当前天气',
  'home.currentTemp': '当前温度',
  'home.viewHourlyTemp': '查看每小时温度预报',
  'home.viewHourlyOf': '查看每小时{label}预报',
  'home.feels': '体感温度',
  'home.wind': '风速',
  'home.windLevel': '第{n}级',
  'home.humidity': '湿度',
  'home.visibility': '能见度',
  'home.pressure': '气压',
  'home.dewpoint': '露点',
  'home.hourlyTab': '今天',
  'home.hourlyMore': '每小时预报',
  'home.chartAria': '天气趋势图',
  'home.daysTab': '10天',
  'home.monthlyMore': '天气预测',
  'home.sunmoon': '日出日落和月相',
  'home.uv': '紫外线指数',
  'home.moon': '月相',
  'home.sunrise': '日出',
  'home.sunset': '日落',
  'home.duration': '{h} 小时 {m} 分钟',
  'home.health': '生活指数',
  'home.warning': '自然灾害预警',
  'home.noWarning': '暂无自然灾害预警',
  'home.addCity': '添加城市',
  'home.myCities': '我的城市',
  'home.tips': '温馨提示',
  'act.outdoor': '户外活动',
  'act.clothing': '穿衣',
  'act.umbrella': '雨伞',
  'act.uv': '紫外线指数',
  'home.source': '数据来源：',
  'home.updated': '更新于 {time}',
  'home.loading': '加载中…',
  'loading.title': 'Weather Live',
  'loading.text': '加载中',
  'home.error': '天气数据加载失败。',
  'home.retry': '重试',

  /* 每小时详情页 */
  'hourly.title': '每小时预报',
  'hourly.selectDate': '选择日期',
  'hourly.changeChart': '更改图表类型',
  'hourly.now': '现在',
  'hourly.tomorrow': '明天',
  'hourly.today': '今天',
  'hourly.feels': '体感温度',
  'hourly.feelsValue': '体感温度: {v}°',
  'hourly.precipProb': '降水概率: {v}%',
  'hourly.gustWind': '阵风: {g} {u}, 风: {w} {u}',
  'hourly.windDir': '风向: {d}',
  'hourly.relHumidity': '相对湿度',
  'hourly.dewPoint': '露点温度',
  'hourly.visibility': '能见度',
  'hourly.pressure': '气压',
  'hourly.airQuality': '空气质量指数',
  'hourly.uv.low': '低',
  'hourly.uv.moderate': '中等',
  'hourly.uv.high': '高',
  'hourly.uv.veryHigh': '很强',
  'hourly.summary.temp.high': '气温为 {hi}° 到 {lo}° 之间。体感温度较高，湿度为 {rh}%。夜间阵风风速最高为 {wind} {unit}。',
  'hourly.summary.temp.low': '气温为 {hi}° 到 {lo}° 之间。体感温度较低，湿度为 {rh}%。夜间阵风风速最高为 {wind} {unit}。',
  'hourly.summary.temp.same': '气温为 {hi}° 到 {lo}° 之间。体感温度与气温相近，湿度为 {rh}%。夜间阵风风速最高为 {wind} {unit}。',
  'hourly.summary.precip': '预计有降雨，累计降水量约 {v} mm。出门请携带雨具。',
  'hourly.summary.precipNone': '预计无明显降水，无需带伞。',
  'hourly.summary.wind': '平均风速为 {v} {unit}，风向多变，且风力稳定。',
  'hourly.summary.humidity': '湿度在 {lo}% 到 {hi}% 之间。',
  'hourly.summary.dewpoint': '露点温度约为 {v}°，与气温接近，体感较为潮湿。',
  'hourly.summary.uv': '紫外线指数为 {v}（{desc}）。建议做好防晒措施。',
  'hourly.summary.visibility': '能见度{level}，约为 {v} {unit}。',
  'hourly.summary.pressure': '气压约为 {v} hPa，天气稳定。',
  'hourly.summary.air': '空气质量指数约为 {v}（{level}）。敏感人群请适当减少户外活动。',
  'hourly.legend.rain': '雨',
  'hourly.legend.snow': '雪',
  'hourly.legend.temp': '温度',
  'hourly.legend.feels': '体感温度',
  'hourly.legend.wind': '风',
  'hourly.legend.gust': '阵风',

  /* 参数名 / 单位 */
  'param.temperature': '温度',
  'param.precipitation': '降水',
  'param.wind': '风',
  'param.humidity': '湿度',
  'param.dewpoint': '露点',
  'param.uv': 'UV',
  'param.visibility': '能见度',
  'param.pressure': '气压',
  'param.air': '空气指数',
  'unit.mm': '{v} mm',
  'unit.kmh': '{v} km/h',
  'unit.speed': '{v} {u}',
  'unit.percent': '{v}%',
  'unit.deg': '{v}°',
  'unit.km': '{v} 公里',
  'unit.distance': '{v} {u}',
  'unit.hpa': '{v} hPa',
  'unit.aqi': '{v} AQI',
  'unit.kmh.name': 'km/h',
  'unit.mph.name': '英里/小时',
  'unit.km.name': '公里',
  'unit.mi.name': '英里',
  'unit.mm.name': 'mm',

  /* 空气质量等级 */
  'aqi.good': '优',
  'aqi.moderate': '良',
  'aqi.sensitive': '轻度污染',
  'aqi.unhealthy': '中度污染',
  'aqi.veryUnhealthy': '重度污染',
  'aqi.hazardous': '严重污染',

  /* 能见度等级（按公里分段，用于图表右侧 Y 轴的阶段标识） */
  'vis.veryPoor': '极差',
  'vis.poor': '差',
  'vis.moderate': '一般',
  'vis.good': '好',
  'vis.veryGood': '很好',
  'vis.excellent': '极好',

  /* 天气地图 */
  'map.title': '天气地图',
  'map.layer.precip': '降水',
  'map.layer.clouds': '云量',
  'map.layer.temp': '温度',
  'map.legend': '图例 · {label}',
  'map.note': '示意图：生产环境此处嵌入 天气雷达瓦片（assets.OPERA.com/weathermapdata）。',

  /* 文章 */
  'article.title': '文章',
  'article.back': '返回',
  'article.share': '分享',
  'article.notfound': '文章不存在。',
  'article.error': '文章加载失败。',
  'article.related': '相关阅读',
  'article.readMin': '{n}分钟阅读',

  /* 资讯 */
  'feed.title': '资讯',
  'feed.hint': '下滑加载更多',
  'feed.error': '资讯加载失败。',
  'feed.retry': '重试',
  'feed.loading': '加载中…',
  'feed.end': '— 没有更多了 —',
  'feed.more': '加载更多',
  'feed.minRead': '{n}分钟读',
  'feed.slide': '第 {n} 张',
  'feed.sponsored': '赞助内容 · {source}',
  'feed.empty': '该频道暂无内容。',
  'feed.adTag': '广告',

  /* 同意条 */
  'consent.aria': '隐私与 Cookie 同意',
  'consent.title': '你的隐私选择',
  'consent.text': '我们使用 Cookie 与本地存储来个性化内容、分析流量并展示相关广告。你可以随时更改选择。',
  'consent.reject': '拒绝',
  'consent.accept': '我接受',

  /* 回到顶部 */
  'top.aria': '回到顶部',

  /* 位置 */
  'loc.title': '更改位置',
  'loc.placeholder': '搜索城市、区县或地点',
  'loc.searchAria': '搜索位置',
  'loc.locating': '正在定位…',
  'loc.useMine': '使用我的当前位置',
  'loc.searching': '搜索中…',
  'loc.empty': '未找到匹配的位置。',
  'loc.saved': '已保存',
  'loc.remove': '移除',
  /* 月度预测 */
  'monthly.title': '天气预测',
  'monthly.sub': '未来 10 天天气预测',
  'monthly.precip': '降水 {v}%',

  /* 时间 */
  'time.justNow': '刚刚',
  'time.minAgo': '{m}分钟前',
  'time.hourAgo': '{h}小时前',
  'time.yesterday': '昨天',
  'time.date': '{M}月{D}日',

  /* 星期 / 相对日 */
  'day.today': '今天',
  'day.tomorrow': '明天',
  'week.long.0': '星期日',
  'week.long.1': '星期一',
  'week.long.2': '星期二',
  'week.long.3': '星期三',
  'week.long.4': '星期四',
  'week.long.5': '星期五',
  'week.long.6': '星期六',
  'week.short.0': '日',
  'week.short.1': '一',
  'week.short.2': '二',
  'week.short.3': '三',
  'week.short.4': '四',
  'week.short.5': '五',
  'week.short.6': '六'
}

const enUS = {
  'app.title': 'Weather',
  'route.home': 'Weather Live',
  'route.location': 'Change location',
  'route.hourly': 'Hourly forecast',
  'route.monthly': '10-day forecast',
  'route.map': 'Weather map',
  'route.activity': 'Life indices',
  'route.feed': 'News',
  'route.article': 'Article',
  'route.privacy': 'Privacy Policy',
  'route.terms': 'Terms of Use',
  'route.about': 'About Us',

  'nav.dialog': 'Navigation menu',
  'nav.open': 'Open navigation menu',
  'nav.close': 'Close menu',
  'nav.home': 'Weather home',
  'nav.hourly': 'Hourly forecast',
  'nav.monthly': '10-day forecast',
  'nav.activity': 'Life indices',
  'nav.privacy': 'Privacy Policy',
  'nav.terms': 'Terms of Use',
  'nav.about': 'About Us',
  'nav.map': 'Weather map',
  'nav.locating': 'Locating…',
  'nav.useLocation': 'Use current location',
  'nav.saved': 'Saved locations',
  'nav.manage': 'Manage locations',
  'nav.language': 'Language',
  'appbar.search': 'Search',
  'appbar.unit': 'Temperature unit',
  'appbar.brand': 'Weather Live',

  'home.current': 'Current weather',
  'home.currentTemp': 'Current temperature',
  'home.viewHourlyTemp': 'View hourly temperature forecast',
  'home.viewHourlyOf': 'View hourly {label} forecast',
  'home.feels': 'Feels like',
  'home.wind': 'Wind',
  'home.windLevel': 'Level {n}',
  'home.humidity': 'Humidity',
  'home.visibility': 'Visibility',
  'home.pressure': 'Pressure',
  'home.dewpoint': 'Dew point',
  'home.hourlyTab': 'Today',
  'home.hourlyMore': 'Hourly forecast',
  'home.chartAria': 'Weather trend chart',
  'home.daysTab': '10 days',
  'home.monthlyMore': '10-day forecast',
  'home.sunmoon': 'Sunrise, sunset & moon',
  'home.uv': 'UV index',
  'home.moon': 'Moon phase',
  'home.sunrise': 'Sunrise',
  'home.sunset': 'Sunset',
  'home.duration': '{h} hr {m} min',
  'home.health': 'Life indices',
  'home.warning': 'Natural hazard alerts',
  'home.noWarning': 'No active natural hazard alerts',
  'home.addCity': 'Add city',
  'home.myCities': 'My cities',
  'home.tips': 'Helpful tips',
  'act.outdoor': 'Outdoor activities',
  'act.clothing': 'Clothing',
  'act.umbrella': 'Umbrella',
  'act.uv': 'UV index',
  'home.source': 'Data source: ',
  'home.updated': 'Updated {time}',
  'home.loading': 'Loading…',
  'loading.title': 'Weather Live',
  'loading.text': 'Loading',
  'home.error': 'Failed to load weather data.',
  'home.retry': 'Retry',

  'hourly.title': 'Hourly forecast',
  'hourly.selectDate': 'Select date',
  'hourly.changeChart': 'Change chart type',
  'hourly.now': 'Now',
  'hourly.tomorrow': 'Tomorrow',
  'hourly.today': 'Today',
  'hourly.feels': 'Feels like',
  'hourly.feelsValue': 'Feels like: {v}°',
  'hourly.precipProb': 'Chance of rain: {v}%',
  'hourly.gustWind': 'Gust: {g} {u}, wind: {w} {u}',
  'hourly.windDir': 'Direction: {d}',
  'hourly.relHumidity': 'Relative humidity',
  'hourly.dewPoint': 'Dew point',
  'hourly.visibility': 'Visibility',
  'hourly.pressure': 'Pressure',
  'hourly.airQuality': 'Air quality index',
  'hourly.uv.low': 'Low',
  'hourly.uv.moderate': 'Moderate',
  'hourly.uv.high': 'High',
  'hourly.uv.veryHigh': 'Very high',
  'hourly.summary.temp.high': 'Temperatures range from {lo}° to {hi}°. It will feel warmer, with humidity around {rh}%. Overnight gusts reach up to {wind} {unit}.',
  'hourly.summary.temp.low': 'Temperatures range from {lo}° to {hi}°. It will feel cooler, with humidity around {rh}%. Overnight gusts reach up to {wind} {unit}.',
  'hourly.summary.temp.same': 'Temperatures range from {lo}° to {hi}°. It will feel close to the air temperature, with humidity around {rh}%. Overnight gusts reach up to {wind} {unit}.',
  'hourly.summary.precip': 'Rain is expected, with about {v} mm in total. Bring an umbrella.',
  'hourly.summary.precipNone': 'No significant precipitation expected — no umbrella needed.',
  'hourly.summary.wind': 'Average wind speed is {v} {unit}, with shifting directions and steady conditions.',
  'hourly.summary.humidity': 'Humidity ranges from {lo}% to {hi}%.',
  'hourly.summary.dewpoint': 'Dew point is around {v}°, close to the air temperature, so it will feel humid.',
  'hourly.summary.uv': 'The UV index is {v} ({desc}). Sun protection is recommended.',
  'hourly.summary.visibility': 'Visibility is {level}, around {v} {unit}.',
  'hourly.summary.pressure': 'Pressure is around {v} hPa, with stable conditions.',
  'hourly.summary.air': 'Air quality index is around {v} ({level}). Sensitive groups should limit time outdoors.',
  'hourly.legend.rain': 'Rain',
  'hourly.legend.snow': 'Snow',
  'hourly.legend.temp': 'Temperature',
  'hourly.legend.feels': 'Feels like',
  'hourly.legend.wind': 'Wind',
  'hourly.legend.gust': 'Gust',

  'param.temperature': 'Temperature',
  'param.precipitation': 'Precipitation',
  'param.wind': 'Wind',
  'param.humidity': 'Humidity',
  'param.dewpoint': 'Dew point',
  'param.uv': 'UV',
  'param.visibility': 'Visibility',
  'param.pressure': 'Pressure',
  'param.air': 'Air quality',
  'unit.mm': '{v} mm',
  'unit.kmh': '{v} km/h',
  'unit.speed': '{v} {u}',
  'unit.percent': '{v}%',
  'unit.deg': '{v}°',
  'unit.km': '{v} km',
  'unit.distance': '{v} {u}',
  'unit.hpa': '{v} hPa',
  'unit.aqi': '{v} AQI',
  'unit.kmh.name': 'km/h',
  'unit.mph.name': 'mph',
  'unit.km.name': 'km',
  'unit.mi.name': 'mi',
  'unit.mm.name': 'mm',

  'aqi.good': 'Good',
  'aqi.moderate': 'Moderate',
  'aqi.sensitive': 'Unhealthy for sensitive groups',
  'aqi.unhealthy': 'Unhealthy',
  'aqi.veryUnhealthy': 'Very unhealthy',
  'aqi.hazardous': 'Hazardous',

  /* Visibility levels (km bands, shown on the chart's right Y axis) */
  'vis.veryPoor': 'Very poor',
  'vis.poor': 'Poor',
  'vis.moderate': 'Moderate',
  'vis.good': 'Good',
  'vis.veryGood': 'Very good',
  'vis.excellent': 'Excellent',

  /* Weather map */
  'map.title': 'Weather map',
  'map.layer.precip': 'Precipitation',
  'map.layer.clouds': 'Clouds',
  'map.layer.temp': 'Temperature',
  'map.legend': 'Legend · {label}',
  'map.note': 'Schematic only — in production this embeds OPERA weather radar tiles (assets.OPERA.com/weathermapdata).',

  /* Article */
  'article.title': 'Article',
  'article.back': 'Back',
  'article.share': 'Share',
  'article.notfound': 'Article not found.',
  'article.error': 'Failed to load article.',
  'article.related': 'Related reading',
  'article.readMin': '{n} min read',

  'feed.title': 'News',
  'feed.hint': 'Scroll to load more',
  'feed.error': 'Failed to load news.',
  'feed.retry': 'Retry',
  'feed.loading': 'Loading…',
  'feed.end': '— No more —',
  'feed.more': 'Load more',
  'feed.minRead': '{n} min read',
  'feed.slide': 'Slide {n}',
  'feed.sponsored': 'Sponsored · {source}',
  'feed.empty': 'No content in this channel yet.',
  'feed.adTag': 'Ad',

  'consent.aria': 'Privacy and cookie consent',
  'consent.title': 'Your privacy choices',
  'consent.text': 'We use cookies and local storage to personalize content, analyze traffic and show relevant ads. You can change your choices at any time.',
  'consent.reject': 'Decline',
  'consent.accept': 'I accept',

  'top.aria': 'Back to top',

  'loc.title': 'Change location',
  'loc.placeholder': 'Search city, district or place',
  'loc.searchAria': 'Search location',
  'loc.locating': 'Locating…',
  'loc.useMine': 'Use my current location',
  'loc.searching': 'Searching…',
  'loc.empty': 'No matching location found.',
  'loc.saved': 'Saved',
  'loc.remove': 'Remove',

  'monthly.title': '10-day forecast',
  'monthly.sub': '10-day weather outlook',
  'monthly.precip': 'Precip {v}%',

  'time.justNow': 'just now',
  'time.minAgo': '{m}m ago',
  'time.hourAgo': '{h}h ago',
  'time.yesterday': 'Yesterday',
  'time.date': '{M}/{D}',

  'day.today': 'Today',
  'day.tomorrow': 'Tomorrow',
  'week.long.0': 'Sunday',
  'week.long.1': 'Monday',
  'week.long.2': 'Tuesday',
  'week.long.3': 'Wednesday',
  'week.long.4': 'Thursday',
  'week.long.5': 'Friday',
  'week.long.6': 'Saturday',
  'week.short.0': 'Sun',
  'week.short.1': 'Mon',
  'week.short.2': 'Tue',
  'week.short.3': 'Wed',
  'week.short.4': 'Thu',
  'week.short.5': 'Fri',
  'week.short.6': 'Sat'
}

/* ------------------------------------------------------------------ */
/* 数据文案词典（接口/mock 返回的中文短语）                              */
/* ------------------------------------------------------------------ */
const phrasesEN = {
  /* 天气状况 */
  '晴朗': 'Sunny',
  '晴': 'Clear',
  '大部晴朗': 'Mostly sunny',
  '局部晴朗': 'Partly sunny',
  '以晴为主': 'Mostly clear',
  '多云': 'Cloudy',
  '阴': 'Overcast',
  '阵雨': 'Showers',
  '局部阵雨': 'Isolated showers',
  '雷阵雨': 'Thunderstorms',
  '雨': 'Rain',
  '小雨': 'Light rain',
  '雪': 'Snow',
  '雨夹雪': 'Rain and snow',
  '雾': 'Fog',
  '霾': 'Haze',

  /* 风 */
  '南风': 'South wind',
  '北风': 'North wind',
  '东风': 'East wind',
  '西风': 'West wind',
  '东南风': 'SE wind',
  '东北风': 'NE wind',
  '西南风': 'SW wind',
  '西北风': 'NW wind',
  '微风': 'Light breeze',
  '级': '',

  /* 月相 */
  '新月': 'New moon',
  '娥眉月': 'Waxing crescent',
  '上弦月': 'First quarter',
  '盈凸月': 'Waxing gibbous',
  '满月': 'Full moon',
  '亏凸月': 'Waning gibbous',
  '下弦月': 'Last quarter',
  '残月': 'Waning crescent',

  /* 紫外线 / 空气 */
  '低': 'Low',
  '中': 'Moderate',
  '高': 'High',
  '极高': 'Very high',
  '空气质量中等': 'Moderate air quality',

  /* 健康活动评级 */
  '非常适宜': 'Ideal',
  '适宜': 'Good',
  '非常适宜': 'Ideal',
  '一般': 'Fair',
  '不太适宜': 'Poor',
  '中等': 'Moderate',
  '低': 'Low',
  '高': 'High',
  '极高': 'Extreme',
  '很高': 'Very high',
  '极端': 'Extreme',
  '透气的衣服': 'Breathable clothing',
  '短袖': 'Short sleeves',
  '长袖': 'Long sleeves',
  '保暖外套': 'Warm coat',
  '无需带伞': 'No umbrella needed',
  '建议带伞': 'Umbrella advised',
  '必须带伞': 'Umbrella essential',

  /* 资讯频道分类 */
  '推荐': 'Recommended',
  '新闻': 'News',
  '娱乐': 'Entertainment',
  '体育': 'Sports',
  '财经': 'Finance',
  '科技': 'Tech',
  '健康': 'Health',
  '旅游': 'Travel',
  '美食': 'Food',

  /* 地图图例 */
  '无': 'None',
  '中雨': 'Moderate rain',
  '大雨': 'Heavy rain',
  '暴雨': 'Torrential rain',
  '少云': 'Few clouds',
  '全云': 'Overcast sky',

  /* 位置 */
  '当前位置': 'Current location',
  '中华人民共和国': 'China',
  '北京市': 'Beijing',
  '海淀区': 'Haidian',
  '中国天气网': 'China Weather',
  '至少 2 小时内没有降水。': 'No precipitation expected for at least 2 hours.',
  '至少 2 小时内没有降水': 'No precipitation for at least 2 hours'
}

const messages = { 'zh-CN': zhCN, 'en-US': enUS }
const phrases = { 'zh-CN': {}, 'en-US': phrasesEN }

export const SUPPORTED_LOCALES = [
  { code: 'zh-CN', label: '简体中文' },
  { code: 'en-US', label: 'English' }
]

/* ------------------------------------------------------------------ */
/* 语言探测与状态                                                       */
/* ------------------------------------------------------------------ */
function detectLocale() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved && messages[saved]) return saved
  } catch { /* storage 不可用时忽略 */ }

  const nav = typeof navigator !== 'undefined' ? navigator : null
  const wanted = nav?.languages?.length ? nav.languages : [nav?.language || 'en-US']
  for (const raw of wanted) {
    if (!raw) continue
    if (messages[raw]) return raw
    const base = raw.split('-')[0].toLowerCase()
    const hit = Object.keys(messages).find((k) => k.split('-')[0].toLowerCase() === base)
    if (hit) return hit
  }
  return 'en-US'
}

export const locale = ref(detectLocale())

export function setLocale(code) {
  if (!messages[code]) return
  locale.value = code
  try { localStorage.setItem(STORAGE_KEY, code) } catch { /* ignore */ }
}

function interpolate(str, vars) {
  if (!vars) return str
  return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] != null ? String(vars[k]) : m))
}

export function t(key, vars) {
  const dict = messages[locale.value] || {}
  const src = messages['zh-CN']
  const raw = dict[key] ?? src[key] ?? key
  return interpolate(raw, vars)
}

/** 翻译接口/mock 返回的数据短语；未命中则原样返回 */
export function tp(text, vars) {
  if (!text) return text ?? ''
  const dict = phrases[locale.value] || {}
  const out = dict[text] ?? text
  return interpolate(out, vars)
}

export function weekLong(i) { return t(`week.long.${i}`) }
export function weekShort(i) { return t(`week.short.${i}`) }

/** 今天 / 明天 / 星期X */
export function dayLabel(offset, date) {
  if (offset === 0) return t('day.today')
  if (offset === 1) return t('day.tomorrow')
  return weekLong(date instanceof Date ? date.getDay() : 0)
}

export function useI18n() {
  return {
    locale: computed(() => locale.value),
    setLocale,
    t, tp, weekLong, weekShort, dayLabel,
    locales: SUPPORTED_LOCALES
  }
}

/** 安装到 app：暴露 $t / $tp 到所有模板，并同步 <html lang> */
export function installI18n(app) {
  app.config.globalProperties.$t = t
  app.config.globalProperties.$tp = tp
  app.config.globalProperties.$locale = locale
  const sync = () => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale.value
  }
  sync()
  watch(locale, sync)
}
